/**
 * FormatterService
 *
 * Provides utility methods for formatting various data types (duration, file sizes).
 * All methods are stateless and pure.
 *
 * Extracted from: ResultDisplay.tsx (lines 25-45)
 */

export class FormatterService {
  /**
   * Formats duration in seconds to MM:SS format
   *
   * @param seconds - Duration in seconds
   * @returns Formatted duration string (e.g., "2:30") or "Unknown" if undefined
   *
   * @example
   * formatDuration(150) // Returns "2:30"
   * formatDuration(45) // Returns "0:45"
   * formatDuration(11.018667) // Returns "0:11"
   * formatDuration(undefined) // Returns "Unknown"
   */
  static formatDuration(seconds?: number): string {
    if (seconds === undefined || seconds === null) return 'Unknown';

    // Round to nearest second for cleaner display
    const roundedSeconds = Math.round(seconds);
    const minutes = Math.floor(roundedSeconds / 60);
    const secs = roundedSeconds % 60;

    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Formats file size in bytes to human-readable format (B, KB, MB, GB)
   *
   * @param sizeInBytes - File size in bytes
   * @returns Formatted file size string (e.g., "2.50 MB") or "Unknown size" if undefined
   *
   * @example
   * formatFileSize(1024) // Returns "1.00 KB"
   * formatFileSize(2500000) // Returns "2.38 MB"
   * formatFileSize(undefined) // Returns "Unknown size"
   */
  static formatFileSize(sizeInBytes?: number): string {
    if (!sizeInBytes) return 'Unknown size';

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = sizeInBytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(unitIndex > 0 ? 2 : 0)} ${units[unitIndex]}`;
  }
}
